import { z } from 'zod';
import { uuidSchema } from './common.schema';

// ---------------------------------------------------------------------------
// Status constants
// ---------------------------------------------------------------------------

export const RECALL_STATUSES = ['ACTIVE', 'CANCELLED'] as const;
export type RecallStatus = (typeof RECALL_STATUSES)[number];

export const RECALL_HOSPITAL_RESPONSE_STATUSES = [
  'PENDING',
  'ACKNOWLEDGED',
  'APPROVED', 'REJECTED',
  'CANCELLED',
  'COMPLETED',
] as const;
export type RecallHospitalResponseStatus = (typeof RECALL_HOSPITAL_RESPONSE_STATUSES)[number];

// ---------------------------------------------------------------------------
// List query schema
// ---------------------------------------------------------------------------

export const RecallHospitalListQuerySchema = z.object({
  /** Filter by one or more hospital-specific recall response statuses (hospital.recall_status field). Accepts a single value or an array. */
  status: z.preprocess(
    (v) => (v === undefined ? undefined : Array.isArray(v) ? v : [v]),
    z.array(z.enum(RECALL_HOSPITAL_RESPONSE_STATUSES)).min(1).optional(),
  ),
  /** Case-insensitive partial match on recall_reference */
  recall_reference: z.string().trim().optional(),
  /** Case-insensitive partial match on recall_title */
  recall_title: z.string().trim().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** Base-64 encoded DynamoDB LastEvaluatedKey for cursor pagination */
  cursor: z.string().optional(),
});

export type RecallHospitalListQuery = z.infer<typeof RecallHospitalListQuerySchema>;

// ---------------------------------------------------------------------------
// Response item schema
// ---------------------------------------------------------------------------

export const RecallHospitalListItemSchema = z.object({
  recall_id: uuidSchema,
  recall_reference: z.string(),
  recall_title: z.string(),
  product_id: z.array(uuidSchema),
  linked_products: z.array(z.string()),
  recall_class: z.string(),
  recall_type: z.string(),
  source_origin: z.string(),
  recall_date: z.string().nullable(),
  affected_brand: z.string().nullable(),
  affected_manufacturer: z.string().nullable(),
  affected_batch_numbers: z.array(z.string()),
  affected_regions: z.array(z.string()),
  reason: z.string(),
  recall_details: z.string().nullable(),
  status: z.string(),
  /** Hospital-specific recall_status from the hospitals array (populated when hospital_id filter is provided) */
  initiated_by: uuidSchema.optional(),
  initiated_at: z.string().optional(),
  activated_at: z.string().nullable(),
  activated_by: uuidSchema.nullable(),
  /** The hospital entry matching the requested hospital_id */
  hospital: z.object({
    hospital_id: uuidSchema,
    recall_status: z.string(),
    notification_status: z.string(),
  }).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type RecallHospitalListItem = z.infer<typeof RecallHospitalListItemSchema>;

export const RecallHospitalListResponseSchema = z.object({
  items: z.array(RecallHospitalListItemSchema),
  next_cursor: z.string().nullable(),
  count: z.number().int(),
});

export type RecallHospitalListResponse = z.infer<typeof RecallHospitalListResponseSchema>;

// ---------------------------------------------------------------------------
// Detail schema — full DynamoDB recall item
// ---------------------------------------------------------------------------

export const RecallDetailSchema = z.object({
  recall_id: uuidSchema,
  recall_reference: z.string(),
  recall_title: z.string(),
  product_id: z.array(uuidSchema),
  linked_products: z.array(z.string()),
  recall_class: z.string(),
  recall_type: z.string(),
  source_origin: z.string(),
  recall_date: z.string().nullable(),
  source_url: z.string().nullable(),
  affected_brand: z.string().nullable(),
  affected_manufacturer: z.string().nullable(),
  affected_batch_numbers: z.array(z.string()),
  affected_regions: z.array(z.string()),
  reason: z.string(),
  recall_action: z.string().optional(),
  recall_details: z.string().nullable(),
  status: z.string(),
  initiated_by: uuidSchema.optional(),
  initiated_at: z.string().optional(),
  activated_at: z.string().nullable(),
  activated_by: uuidSchema.nullable(),
  /** The hospital entry matching the requested hospital_id */
  hospital: z.object({
    hospital_id: uuidSchema,
    recall_status: z.string(),
    notification_status: z.string(),
  }).nullable(),
  is_deleted: z.boolean(),
  deleted_at: z.string().nullable(),
  deleted_by: uuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type RecallDetail = z.infer<typeof RecallDetailSchema>;

// ---------------------------------------------------------------------------
// Affected patients response schema
// ---------------------------------------------------------------------------

export const FEED_PREP_STATUSES = [
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'ON_HOLD',
] as const;
export type FeedPrepStatus = (typeof FEED_PREP_STATUSES)[number];

export const AffectedPatientItemSchema = z.object({
  patient_id: uuidSchema,
  patient_name: z.string(),
  mrn: z.string(),
  prep_id: uuidSchema,
  prep_status: z.string(),
  order_id: uuidSchema.nullable(),
  batch_number: z.string(),
  unit_id: uuidSchema,
  started_at: z.string(),
  completed_at: z.string().nullable(),
});

export type AffectedPatientItem = z.infer<typeof AffectedPatientItemSchema>;

// Blocked feed_prep_splits (not DELIVERED, no delivery_id)
export const BlockedSplitItemSchema = z.object({
  split_id: uuidSchema,
  prep_id: uuidSchema,
  patient_id: uuidSchema.nullable(),
  patient_name: z.string().nullable(),
  mrn: z.string().nullable(),
  split_status: z.string(),
  location: z.string().nullable(),
  syringe_label_barcode: z.string().nullable(),
  scheduled_time: z.string().nullable(),
  batch_number: z.string().nullable(),
  volume_ml: z.number().nullable(),
});
export type BlockedSplitItem = z.infer<typeof BlockedSplitItemSchema>;

// Delivered feeds touching the recalled batch/product (last 30 days)
export const DeliveredFeedItemSchema = z.object({
  patient_id: uuidSchema,
  patient_name: z.string(),
  mrn: z.string(),
  location: z.string().nullable(),
  feeds_delivered: z.number().int(),
  first_delivered_at: z.string(),
  last_delivered_at: z.string(),
});
export type DeliveredFeedItem = z.infer<typeof DeliveredFeedItemSchema>;

export const AffectedPatientsResponseSchema = z.object({
  recall_id: uuidSchema,
  recall_hospital_id: uuidSchema.nullable(),
  hospital_id: uuidSchema,
  affected_batch_numbers: z.array(z.string()),
  blocked_splits: z.array(BlockedSplitItemSchema),
  delivered_feeds: z.array(DeliveredFeedItemSchema),
});

export type AffectedPatientsResponse = z.infer<typeof AffectedPatientsResponseSchema>;

// ---------------------------------------------------------------------------
// PUT /:id/status — request body + response
// ---------------------------------------------------------------------------

export const RecallStatusUpdateActionSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('APPROVED'),
  }),
  z.object({
    status: z.literal('REJECTED'),
    rejection_reason: z.string().trim().min(1, 'Rejection reason is required'),
  }),
]);

export type RecallStatusUpdateAction = z.infer<typeof RecallStatusUpdateActionSchema>;

export const RecallStatusUpdateResultSchema = z.object({
  recall_hospital_id: uuidSchema,
  recall_id: uuidSchema,
  hospital_id: uuidSchema,
  status: z.string(),
  response_status: z.string(),
  affected_quantity: z.string().nullable(),
  quarantined_quantity: z.string().nullable(),
  blocked_feed_count: z.number().int().optional(),
});

export type RecallStatusUpdateResult = z.infer<typeof RecallStatusUpdateResultSchema>;

// ---------------------------------------------------------------------------
// PUT /:id/hospital-status — update hospital recall_status in DynamoDB
// ---------------------------------------------------------------------------

export const UpdateHospitalRecallStatusSchema = z.object({
  hospital_id: uuidSchema,
  status: z.enum(['APPROVED', 'REJECTED']),
  reason: z.string().trim().optional(),
  acknowledged_by: uuidSchema.optional(),
});

export type UpdateHospitalRecallStatus = z.infer<typeof UpdateHospitalRecallStatusSchema>;

export const UpdateHospitalRecallStatusResultSchema = z.object({
  recall_id: uuidSchema,
  hospital_id: uuidSchema,
  recall_status: z.string(),
  notification_status: z.string(),
  approved_by: uuidSchema.nullable(),
  approved_at: z.string().nullable(),
  inventory_updated: z.number().int().optional(),
  units_updated: z.number().int().optional(),
  batches_updated: z.number().int().optional(),
  splits_blocked: z.number().int().optional(),
  schedules_deactivated: z.number().int().optional(),
});

export type UpdateHospitalRecallStatusResult = z.infer<typeof UpdateHospitalRecallStatusResultSchema>;

// ---------------------------------------------------------------------------
// POST /recalls/hospitals — create product_recall_hospitals row
// ---------------------------------------------------------------------------

export const CreateRecallHospitalSchema = z.object({
  recall_id: uuidSchema,
  hospital_id: uuidSchema,
  notification_message_id: uuidSchema.nullish(),
  notified_by: uuidSchema.optional(),
});

export type CreateRecallHospital = z.infer<typeof CreateRecallHospitalSchema>;

export const CreateRecallHospitalResultSchema = z.object({
  recall_hospital_id: uuidSchema,
  recall_id: uuidSchema,
  hospital_id: uuidSchema,
  notification_message_id: uuidSchema.nullable(),
  response_status: z.string(),
  notified_at: z.string().nullable(),
  created_at: z.string(),
});

export type CreateRecallHospitalResult = z.infer<typeof CreateRecallHospitalResultSchema>;
